import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router';
import { useUser } from '../state/UserContext';

/** Concrete practice milestones (see the Omnist Development Plan's "Progress and motivation") --
 * primary UI leads with these instead of a spiritual-authority-sounding rank title. A milestone
 * already reached shows as "X practice days"; short of the first one, an honest running count. */
function formatPracticeMilestoneLabel(totalPracticeDays: number, milestone: number): string {
  if (milestone > 0) return `${milestone} practice days`;
  if (totalPracticeDays > 0) return `${totalPracticeDays} practice day${totalPracticeDays === 1 ? '' : 's'}`;
  return 'Just beginning';
}

const NAV_ITEMS = [
  { to: '/home', labelKey: 'nav.home', icon: '🏠' },
  { to: '/practices', labelKey: 'nav.practices', icon: '🧘' },
  { to: '/calendar', labelKey: 'nav.calendar', icon: '📅' },
  { to: '/library', labelKey: 'nav.library', icon: '📚' },
  { to: '/journal', labelKey: 'nav.journal', icon: '📓' },
  { to: '/leaderboard', labelKey: 'nav.leaderboard', icon: '🏆' },
  { to: '/settings', labelKey: 'nav.settings', icon: '⚙️' },
] as const;

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * A slim top bar (hamburger + wordmark) plus an overlay drawer that slides in from the left --
 * same interaction as theclowncr.vip and Ember's site nav, restyled to Omnist's own theme tokens
 * rather than their fixed dark palette. One nav pattern at every viewport size.
 *
 * Review fix F7 (WAI-ARIA modal dialog pattern): closed, the drawer is `inert` (and
 * `visibility: hidden` in CSS) -- nothing inside is focusable or exposed to assistive tech. Open,
 * it is a modal dialog: the top bar, main content and skip link become `inert`, Tab/Shift+Tab wrap
 * inside the drawer, and every close path -- close button, Escape, backdrop, choosing a link, or
 * any other route change -- goes through `close()`, which returns focus to the menu button.
 */
export function NavBar() {
  const { me } = useUser();
  const { t } = useTranslation();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const isOpenRef = useRef(false);
  const restoreFocusRef = useRef(false);
  const headerRef = useRef<HTMLElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const openButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const close = useCallback(() => {
    if (!isOpenRef.current) return;
    restoreFocusRef.current = true;
    setIsOpen(false);
  }, []);

  // Route change while open (e.g. browser back): close through the same path.
  const previousPathRef = useRef(location.pathname);
  useEffect(() => {
    if (previousPathRef.current === location.pathname) return;
    previousPathRef.current = location.pathname;
    close();
  }, [location.pathname, close]);

  useEffect(() => {
    const drawer = drawerRef.current;
    const background = [headerRef.current, document.getElementById('main-content'), document.getElementById('skip-link')];
    if (isOpen) {
      drawer?.removeAttribute('inert');
      background.forEach((el) => el?.setAttribute('inert', ''));
      document.body.style.overflow = 'hidden';
      closeButtonRef.current?.focus();
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          close();
        }
      };
      document.addEventListener('keydown', onKeyDown);
      return () => document.removeEventListener('keydown', onKeyDown);
    }
    drawer?.setAttribute('inert', '');
    background.forEach((el) => el?.removeAttribute('inert'));
    document.body.style.overflow = '';
    if (restoreFocusRef.current) {
      restoreFocusRef.current = false;
      openButtonRef.current?.focus();
    }
    return undefined;
  }, [isOpen, close]);

  // Never leave the page inert/locked if the shell unmounts (e.g. logging out mid-open).
  useEffect(
    () => () => {
      document.getElementById('main-content')?.removeAttribute('inert');
      document.getElementById('skip-link')?.removeAttribute('inert');
      document.body.style.overflow = '';
    },
    [],
  );

  const trapFocus = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !drawerRef.current) return;
    const items = Array.from(drawerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <>
      <header ref={headerRef} className="top-bar">
        {me && (
          <span
            className="header-streak"
            title={`${me.currentStreak}-day streak · ${formatPracticeMilestoneLabel(me.totalPracticeDays, me.practiceMilestone)}`}
          >
            <span aria-hidden="true">🔥</span> {me.currentStreak}
            <span className="header-rank">{formatPracticeMilestoneLabel(me.totalPracticeDays, me.practiceMilestone)}</span>
          </span>
        )}
        <button
          ref={openButtonRef}
          type="button"
          className="hamburger-btn"
          aria-label={t('nav.openMenu')}
          aria-expanded={isOpen}
          aria-controls="nav-drawer"
          aria-haspopup="dialog"
          onClick={() => setIsOpen(true)}
        >
          <span />
          <span />
          <span />
        </button>
        <span className="top-bar-brand">
          <span className="logo-badge">
            <img src="/logo-face.png" alt="" className="logo-face" />
          </span>
          <span className="wordmark">Omnist</span>
        </span>
      </header>

      <div className={`drawer-overlay${isOpen ? ' open' : ''}`} onClick={close} aria-hidden="true" />

      <div
        ref={drawerRef}
        id="nav-drawer"
        className={`nav-drawer${isOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="nav-drawer-title"
        onKeyDown={trapFocus}
      >
        <div className="nav-drawer-header">
          <div className="nav-drawer-brand">
            <span className="logo-badge">
              <img src="/logo-face.png" alt="" className="logo-face" />
            </span>
            <div>
              <div id="nav-drawer-title" className="nav-drawer-title">{t('nav.menu')}</div>
              <div className="nav-drawer-subtitle wordmark">Omnist</div>
            </div>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="drawer-close-btn"
            aria-label={t('nav.closeMenu')}
            onClick={close}
          >
            ✕
          </button>
        </div>

        <nav className="nav-drawer-links" aria-label={t('a11y.primaryNav')}>
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
              onClick={close}
            >
              <span className="nav-icon" aria-hidden="true">{item.icon}</span>
              <span>{t(item.labelKey)}</span>
            </NavLink>
          ))}

          {me?.isAdmin && (
            <NavLink to="/admin" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`} onClick={close}>
              <span className="nav-icon" aria-hidden="true">🛠️</span>
              <span>{t('nav.admin')}</span>
            </NavLink>
          )}
        </nav>
      </div>
    </>
  );
}
